import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DrawableMannequin from './DrawableMannequin';
import styleService from '../../services/styleService';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFile } from '../../services/uploadService';
import { compressImage } from '../../utils/imageCompression';
import FileUploadField from '../UI/FileUploadField/FileUploadField';
import './QuoteRequestModal.css';

const QuoteRequestModal = ({ isOpen, onClose, artistProfileId, artistName, onSubmit, initialArtwork = null }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        style: '',
        bodyPart: '',
        size: '',
        description: '',
        referenceImages: [],
        mannequinData: null,
        height: 170,
        artworkId: null
    });

    const [loading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [availableStyles, setAvailableStyles] = useState([]);
    const [artistStyles, setArtistStyles] = useState([]);
    const { token } = useAuth();

    useEffect(() => {
        if (isOpen) {
            styleService.getAllStyles()
                .then(setAvailableStyles)
                .catch(console.error);

            if (artistProfileId) {
                styleService.getArtistStyles(artistProfileId)
                    .then(setArtistStyles)
                    .catch(console.error);
            }

            if (initialArtwork) {
                setFormData(prev => ({
                    ...prev,
                    style: initialArtwork.category || '',
                    referenceImages: initialArtwork.imageUrl ? [initialArtwork.imageUrl] : [],
                    artworkId: initialArtwork.id
                }));
            } else {
                setFormData({
                    style: '',
                    bodyPart: '',
                    size: '',
                    description: '',
                    referenceImages: [],
                    mannequinData: null,
                    height: 170,
                    artworkId: null
                });
            }
        }
    }, [isOpen, artistProfileId, initialArtwork]);

    React.useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAreaChange = (areaData) => {
        setFormData(prev => ({
            ...prev,
            bodyPart: areaData.bodyPart || prev.bodyPart,
            size: areaData.size || prev.size,
            mannequinData: areaData.mannequinData || prev.mannequinData
        }));
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            const uploadPromises = files.map(async (file) => {
                const compressed = await compressImage(file);
                return await uploadFile(compressed, token, 'quotes');
            });

            const urls = await Promise.all(uploadPromises);
            setFormData(prev => ({
                ...prev,
                referenceImages: [...prev.referenceImages, ...urls]
            }));
        } catch (err) {
            console.error('Error uploading reference images:', err);
            setError(err.message || t('artwork_error'));
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleRemoveReferenceImage = (index) => {
        setFormData(prev => ({
            ...prev,
            referenceImages: prev.referenceImages.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await onSubmit({ ...formData, artistProfileId });
            onClose();
        } catch (err) {
            setError(t('submit_quote_error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <button className="close-button" onClick={onClose}>&times;</button>
                <h2>{t('quote_with', { name: artistName })}</h2>
                {initialArtwork && (
                    <p className="artwork-context-label" style={{ color: '#d90429', marginBottom: '15px', fontWeight: 'bold' }}>
                        {t('request_for_artwork', { title: initialArtwork.title || t('this_artwork') })}
                    </p>
                )}
                {error && <p className="error-message">{error}</p>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>{t('your_height_optional')}</label>
                        <input
                            type="number"
                            name="height"
                            value={formData.height || ''}
                            onChange={handleChange}
                            placeholder="Ex: 175"
                        />
                        <small className="field-hint">{t('height_hint')}</small>
                    </div>

                    <div className="form-group">
                        <label>{t('draw_tattoo_area')}</label>
                        <DrawableMannequin
                            onAreaChange={handleAreaChange}
                            heightCm={formData.height}
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('style')}</label>
                        <select
                            name="style"
                            value={formData.style}
                            onChange={handleChange}
                            required
                            className="style-select"
                        >
                            <option value="">{t('select_style')}</option>
                            {artistStyles.length > 0 && (
                                <optgroup label={t('artist_specialties')}>
                                    {artistStyles.map(s => (
                                        <option key={s.id} value={s.name}>
                                            {t(`style_${s.name.toLowerCase().replace(/\s+/g, '_')}`) || s.name}
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                            <optgroup label={t('all_styles') || 'Todos os Estilos'}>
                                {availableStyles.map(s => (
                                    <option key={s.id} value={s.name}>
                                        {t(`style_${s.name.toLowerCase().replace(/\s+/g, '_')}`) || s.name}
                                    </option>
                                ))}
                            </optgroup>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>{t('idea_description')}</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            required
                            rows="4"
                            placeholder={t('describe_idea_placeholder')}
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('reference_photos')}</label>
                        <div className="reference-uploads-grid">
                            {formData.referenceImages.map((img, idx) => (
                                <div key={idx} className="reference-item-container">
                                    <FileUploadField
                                        value={img}
                                        onChange={() => { }}
                                        uploading={false}
                                        type="square"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveReferenceImage(idx)}
                                        className="remove-reference-btn"
                                        title={t('remove')}
                                    >
                                        &times;
                                    </button>
                                </div>
                            ))}

                            {formData.referenceImages.length < 5 && (
                                <FileUploadField
                                    label={formData.referenceImages.length === 0 ? t('add_reference') : ""}
                                    onChange={handleFileChange}
                                    uploading={isUploading}
                                    type="square"
                                    multiple={true}
                                    hint={formData.referenceImages.length === 0 ? t('reference_hint') : ""}
                                />
                            )}
                        </div>
                    </div>
                    <div className="inquiry-disclaimer-box">
                        <p>{t('inquiry_disclaimer')}</p>
                    </div>
                    <div className="form-actions">
                        <button type="button" onClick={onClose} className="cancel-btn">{t('cancel')}</button>
                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? t('sending') : t('send_request')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default QuoteRequestModal;
